import { useEffect } from 'react';

export const SITE_URL  = 'https://nimaisolutions.in';
export const SITE_NAME = 'Nimai Solutions';
export const OG_IMAGE  = `${SITE_URL}/logo.png`;

/**
 * Lightweight SEO hook — no extra packages.
 * Sets document.title, meta description, OG, Twitter Card, canonical & JSON-LD per page.
 *
 * @param {object} cfg
 * @param {string}  cfg.title       - Full page <title>
 * @param {string}  cfg.description - Meta description (≤ 160 chars)
 * @param {string}  [cfg.canonical] - Canonical URL (defaults to current pathname)
 * @param {string}  [cfg.keywords]  - Comma-separated keywords
 * @param {string}  [cfg.ogImage]   - Absolute URL for og:image
 * @param {object}  [cfg.schema]    - JSON-LD schema object (defined at module scope to be stable)
 */
const useSEO = ({ title, description, canonical, keywords, ogImage, schema }) => {
    useEffect(() => {
        /* ── helpers ─────────────────────────────────────── */
        const setMeta = (attrKey, attrVal, content) => {
            let el = document.querySelector(`meta[${attrKey}="${attrVal}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attrKey, attrVal);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        const setLink = (rel, href) => {
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        const canonicalHref = canonical || `${SITE_URL}${window.location.pathname}`;
        const imageHref     = ogImage   || OG_IMAGE;

        /* ── 1. Title ──────────────────────────────────── */
        document.title = title;

        /* ── 2. Core meta ──────────────────────────────── */
        setMeta('name', 'description',          description);
        setMeta('name', 'robots',               'index, follow, max-snippet:-1, max-image-preview:large');
        setMeta('name', 'author',               SITE_NAME);
        if (keywords) setMeta('name', 'keywords', keywords);

        /* ── 3. Geo / local SEO ────────────────────────── */
        setMeta('name', 'geo.region',           'IN-GJ');
        setMeta('name', 'geo.placename',        'Vadodara');
        setMeta('name', 'geo.position',         '22.3072;73.1812');
        setMeta('name', 'ICBM',                 '22.3072, 73.1812');

        /* ── 4. Open Graph ─────────────────────────────── */
        setMeta('property', 'og:type',          'website');
        setMeta('property', 'og:site_name',     SITE_NAME);
        setMeta('property', 'og:locale',        'en_IN');
        setMeta('property', 'og:title',         title);
        setMeta('property', 'og:description',   description);
        setMeta('property', 'og:url',           canonicalHref);
        setMeta('property', 'og:image',         imageHref);
        setMeta('property', 'og:image:width',   '1200');
        setMeta('property', 'og:image:height',  '630');
        setMeta('property', 'og:image:alt',     `${SITE_NAME} — ${title}`);

        /* ── 5. Twitter Card ───────────────────────────── */
        setMeta('name', 'twitter:card',         'summary_large_image');
        setMeta('name', 'twitter:site',         '@NimaiSolutions');
        setMeta('name', 'twitter:title',        title);
        setMeta('name', 'twitter:description',  description);
        setMeta('name', 'twitter:image',        imageHref);

        /* ── 6. Canonical ──────────────────────────────── */
        setLink('canonical', canonicalHref);

        /* ── 7. JSON-LD (page-level, replaces previous) ── */
        if (schema) {
            let script = document.querySelector('script[data-page-schema]');
            if (!script) {
                script = document.createElement('script');
                script.setAttribute('type', 'application/ld+json');
                script.setAttribute('data-page-schema', 'true');
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(schema);
        }

        /* ── 8. Scroll to top on page change ───────────── */
        window.scrollTo(0, 0);

    // title/description/canonical are the only values that change per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, description, canonical]);
};

export default useSEO;
